import { useState, useEffect, useRef } from 'react';
import AcademicExpirationService, { AcademicExpirationStatus } from '../services/AcademicExpirationService';
import academicExpirationNotificationService from '../services/AcademicExpirationNotificationService';
import { useAcademicApplication } from './useAcademicApplication';
import { RenewalFormData } from '../components/Academic/AcademicRenewalForm';
import { CancellationFormData } from '../components/Academic/AcademicCancellationForm';
import { useAcademicFeedback } from './useAcademicFeedback';
import { useAuthStore } from '../store/authStore';

interface CognitoUser {
  email: string;
  'custom:created_at'?: string;
  'custom:academic_renewed_at'?: string;
  [key: string]: any;
}

interface TagInfo {
  text: string;
  type: 'warning' | 'danger';
  urgent: boolean;
}

interface RenewalResult {
  success: boolean;
  message: string;
}

interface UseAcademicExpirationReturn {
  expirationStatus: AcademicExpirationStatus | null;
  showWarningModal: boolean;
  warningType: string | null;
  loading: boolean;
  isAcademic: boolean;
  shouldShowExpirationTag: boolean;
  tagInfo: TagInfo | null;
  handleRenewal: (formData?: RenewalFormData) => Promise<RenewalResult>;
  handleCancellation: (formData?: CancellationFormData) => Promise<RenewalResult>;
  dismissWarning: () => void;
  openModal: () => void;
  setShowWarningModal: (show: boolean) => void;
  clearPendingRenewalConfirmation: () => void;
  // New states
  hasAppliedForRenewal: boolean;
  hasCancelledAccount: boolean;
  wasRenewalDenied: boolean;
  wasRenewalApproved: boolean;
  currentAcademicPlan: string | null;
}

/**
 * Custom hook for managing academic expiration state and logic
 */
export const useAcademicExpiration = (
  user: CognitoUser | null,
  isAuthenticated: boolean = false,
): UseAcademicExpirationReturn => {
  // Feature always enabled in production (no feature flag)
  const isAcademicExpirationEnabled = true;

  const [expirationStatus, setExpirationStatus] = useState(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [warningType, setWarningType] = useState(null);
  const [loading, setLoading] = useState(false);
  const notificationRecordedRef = useRef(false);
  const accountStatusCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const denialProcessedRef = useRef(false);

  const getPendingRenewalKey = () => (user?.email ? `pending-renewal-${user.email}` : null);
  const [hasPendingRenewalConfirmation, setHasPendingRenewalConfirmation] = useState(() => {
    const key = getPendingRenewalKey();
    if (!key) return false;
    const stored = localStorage.getItem(key);
    return stored ? Date.now() - parseInt(stored) < 24 * 60 * 60 * 1000 : false; // 24 hours
  });

  const { submitRenewalFeedback, submitCancellationFeedback } = useAcademicFeedback();
  const { initializeAuth } = useAuthStore();

  const forceRefreshUserAttributes = async () => {
    try {
      const { getCurrentUser } = await import('aws-amplify/auth');
      const { refreshSession } = await import('../services/RefreshSession');

      const user = await getCurrentUser();
      const session = await refreshSession({ forceRefresh: true });
      const userInfo = session.tokens.idToken.payload;

      const userWithAttributes = {
        ...userInfo,
        ...user,
      };

      const authStore = useAuthStore.getState();
      authStore.setUser(userWithAttributes);
      return userWithAttributes;
    } catch (error) {
      console.error('Failed to refresh user attributes:', error);
      throw error;
    }
  };

  const checkIfDenied = async (): Promise<boolean> => {
    if (!user?.email) {
      return false;
    }

    try {
      const academicApplicationService = (await import('../services/AcademicApplicationService')).default;
      const statusResponse = await academicApplicationService.getAcademicStatus(user.email);

      const deniedTags = ['6845', '6879']; // Free Academic: Denied, China: Denied
      const hasDeniedTag = statusResponse.activeCampaignTags?.some(tag => deniedTags.includes(tag));

      if (hasDeniedTag) {
        return true;
      }

      return false;
    } catch (error) {
      console.error('[checkIfDenied] Error checking if user was denied:', error);
      return false;
    }
  };

  const handleRenewalDenied = async () => {
    denialProcessedRef.current = true;

    if (accountStatusCheckIntervalRef.current) {
      clearInterval(accountStatusCheckIntervalRef.current);
      accountStatusCheckIntervalRef.current = null;
    }

    const Swal = (await import('sweetalert2')).default;
    await Swal.fire({
      title: 'Renewal Application Denied',
      text: 'Your renewal application has been reviewed and unfortunately was not approved. Your account has been deactivated and you will be logged out.',
      icon: 'error',
      confirmButtonText: 'OK',
      allowOutsideClick: false,
      allowEscapeKey: false,
    });

    const { signOut } = await import('aws-amplify/auth');
    await signOut();

    window.location.href = '/';
  };

  const startAccountStatusPolling = () => {
    if (accountStatusCheckIntervalRef.current) {
      clearInterval(accountStatusCheckIntervalRef.current);
    }

    checkIfDenied().then(isDenied => {
      if (isDenied && !denialProcessedRef.current) {
        handleRenewalDenied();
      }
    });

    accountStatusCheckIntervalRef.current = setInterval(async () => {
      const isDenied = await checkIfDenied();
      if (isDenied && !denialProcessedRef.current) {
        handleRenewalDenied();
      }
    }, 30000); // 30 seconds
  };

  const stopAccountStatusPolling = () => {
    if (accountStatusCheckIntervalRef.current) {
      clearInterval(accountStatusCheckIntervalRef.current);
      accountStatusCheckIntervalRef.current = null;
    }
  };

  // Use the existing academic application hook to get tags and determine if user is academic
  const { activeCampaignTags, loadingStatus, nextPaymentDue } = useAcademicApplication(
    user?.email,
    isAuthenticated,
  );

  // Academic tags only: 6877 (China Free), 6844 (Free), 6914 (Standard Academic), 6913 (Advanced Academic).
  // Do NOT include commercial roles (ROLE_PROFESSIONAL, ROLE_STANDARD) — expiration tag is for academics only.
  const academicTags = ['6877', '6844', '6914', '6913'];
  const isAcademicByTags = activeCampaignTags.some(tag => academicTags.includes(tag));

  const cognitoGroups = user?.['cognito:groups'] || [];
  const isAcademicByGroup = cognitoGroups.includes('ROLE_ACADEMIC');

  const isAcademic = isAcademicByTags || isAcademicByGroup;

  // Check application and approval states (Cognito + AC: tags 6915/6916 may lag until AC syncs)
  const cognitoRenewalPending = user?.['custom:is_renewal'] === 'true' || !!user?.['custom:renewal_app_at'];
  const hasAppliedForRenewal =
    activeCampaignTags.includes('6915') || activeCampaignTags.includes('6916') || cognitoRenewalPending;
  const hasCancelledAccount = !!user?.['custom:academic_cancelled_at'];
  const wasRenewalDenied = activeCampaignTags.includes('6845') || activeCampaignTags.includes('6879'); // Denied tags
  const wasRenewalApproved = !!user?.['custom:renewal_approved_at'] || !!user?.['custom:renewal_conf_at'];

  // Determine current academic plan
  let currentAcademicPlan = null;
  if (activeCampaignTags.includes('6877')) currentAcademicPlan = 'China Free Academic';
  else if (activeCampaignTags.includes('6844')) currentAcademicPlan = 'Free Academic';
  else if (activeCampaignTags.includes('6914')) currentAcademicPlan = 'Standard Academic';
  else if (activeCampaignTags.includes('6913')) currentAcademicPlan = 'Advanced Academic';

  const userCreatedAt = user?.['custom:created_at'];
  const userRenewedAt = user?.['custom:academic_renewed_at'];
  const userNotifiedAt = user?.['custom:notified_at'];
  /** Prefer id token (updated at renewal) over AC API, which can lag after confirmRenewal */
  const effectiveNextPaymentDue = user?.['custom:next_payment_due'] || nextPaymentDue;

  // Calculate expiration status
  useEffect(() => {
    if (!isAcademicExpirationEnabled) {
      setExpirationStatus(null);
      setShowWarningModal(false);
      setWarningType(null);
      return;
    }

    if (isAuthenticated && isAcademic && userCreatedAt && !loadingStatus) {
      const status = AcademicExpirationService.calculateExpirationStatus(
        userCreatedAt,
        userRenewedAt,
        effectiveNextPaymentDue || undefined,
        userNotifiedAt,
      );
      setExpirationStatus(status);

      if (hasAppliedForRenewal && hasPendingRenewalConfirmation) {
        const key = getPendingRenewalKey();
        if (key) {
          localStorage.removeItem(key);
          setHasPendingRenewalConfirmation(false);
        }
      }

      // Determine which modal to show based on user state
      let shouldShowAutomatically = false;
      let warnType = null;

      if (hasPendingRenewalConfirmation) {
        warnType = 'waiting-confirmation';
        shouldShowAutomatically = !AcademicExpirationService.hasShownWarningToday(warnType);
      } else if (wasRenewalApproved) {
        // Show approval confirmation modal
        warnType = 'renewal-approved';
        shouldShowAutomatically = !AcademicExpirationService.hasShownWarningToday(warnType);
      } else if (hasAppliedForRenewal && !wasRenewalDenied) {
        // Show reviewing application modal
        warnType = 'reviewing-application';
        shouldShowAutomatically = !AcademicExpirationService.hasShownWarningToday(warnType);
      } else if (hasCancelledAccount) {
        // Show cancelled account modal
        warnType = 'account-cancelled';
        shouldShowAutomatically = !AcademicExpirationService.hasShownWarningToday(warnType);
      } else if (wasRenewalDenied) {
        // Show renewal denied modal
        warnType = 'renewal-denied';
        shouldShowAutomatically = !AcademicExpirationService.hasShownWarningToday(warnType);
      } else {
        const shouldWarnAboutExpiration = AcademicExpirationService.shouldShowWarning(status);
        if (shouldWarnAboutExpiration) {
          warnType = AcademicExpirationService.getWarningType(status);
          shouldShowAutomatically = !userNotifiedAt;
        }
      }

      if (shouldShowAutomatically && warnType) {
        setWarningType(warnType);
        setShowWarningModal(true);
        AcademicExpirationService.markWarningShown(warnType);
      }
    } else {
      setExpirationStatus(null);
    }
  }, [
    isAuthenticated,
    isAcademic,
    userCreatedAt,
    userRenewedAt,
    effectiveNextPaymentDue,
    userNotifiedAt,
    loadingStatus,
    isAcademicExpirationEnabled,
    hasPendingRenewalConfirmation,
    hasAppliedForRenewal,
    wasRenewalApproved,
    wasRenewalDenied,
    hasCancelledAccount,
  ]);

  useEffect(() => {
    if (!isAcademicExpirationEnabled || !isAuthenticated || !isAcademic) {
      stopAccountStatusPolling();
      return;
    }

    const shouldStartPolling =
      hasAppliedForRenewal && !wasRenewalApproved && !hasCancelledAccount && !denialProcessedRef.current;

    if (shouldStartPolling) {
      startAccountStatusPolling();
    } else {
      stopAccountStatusPolling();
    }

    return () => {
      stopAccountStatusPolling();
    };
  }, [
    isAuthenticated,
    isAcademic,
    hasAppliedForRenewal,
    wasRenewalApproved,
    hasCancelledAccount,
    isAcademicExpirationEnabled,
  ]);

  /**
   * Handle renewal process
   */
  const handleRenewal = async (formData?: RenewalFormData) => {
    if (!user?.email) return;

    setLoading(true);
    try {
      let result: { success: boolean; message: string; error?: string };

      if (formData) {
        const groups = user?.['cognito:groups'];
        const plan = Array.isArray(groups) ? groups.join(',') : groups ? String(groups) : 'academic';
        const userInfo = {
          name: user['custom:name'] || user.name || user.given_name || user.family_name || 'Academic User',
          email: user.email,
          organization:
            user['custom:company'] || user['custom:organization'] || user.organization || 'Not specified',
          plan,
        };

        const feedbackOk = await submitRenewalFeedback(formData, userInfo);
        if (!feedbackOk) {
          return {
            success: false,
            message: 'Could not complete renewal submission. Please try again.',
          };
        }
        result = { success: true, message: 'Academic renewal process initiated.' };
      } else {
        result = await AcademicExpirationService.renewAcademicAccess(user.email);
      }

      if (result.success) {
        const key = getPendingRenewalKey();
        if (key) {
          localStorage.setItem(key, Date.now().toString());
          setHasPendingRenewalConfirmation(true);
        }

        try {
          await forceRefreshUserAttributes();
        } catch (e) {
          console.warn('Session refresh after renewal submit failed (user may need to re-login):', e);
        }

        setShowWarningModal(false);
        if (warningType) {
          AcademicExpirationService.markWarningShown(warningType);
        }
        return { success: true, message: result.message };
      }

      return { success: false, message: result.message };
    } catch (error) {
      console.error('Error handling renewal:', error);
      return {
        success: false,
        message: 'An error occurred while processing your renewal request.',
      };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle account cancellation
   */
  const handleCancellation = async (formData?: CancellationFormData) => {
    if (!user?.email) return;

    // Check if user is already expired or still has days remaining
    const isAlreadyExpired = expirationStatus?.isExpired || false;
    const daysRemaining = expirationStatus?.daysUntilExpiration || 0;

    // User not yet expired - show warning about immediate logout
    if (!isAlreadyExpired && daysRemaining > 0) {
      const Swal = (await import('sweetalert2')).default;
      const confirmResult = await Swal.fire({
        title: 'Immediate Logout Warning',
        html: `If you cancel your account now, you will be logged out immediately and will not be able to access the platform anymore.<br><br>You have <strong>${daysRemaining} days</strong> remaining in your current plan. If you want to continue using the app during this time, you can close this modal and your account will be automatically deactivated when the time expires.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Cancel Account Now',
        cancelButtonText: 'Keep Using My Account',
        customClass: {
          confirmButton: 'btn-gradient-pink',
          cancelButton: 'btn-gradient-blue',
        },
      });

      if (!confirmResult.isConfirmed) {
        return { success: false, message: 'Cancellation aborted by user' };
      }
    }

    setLoading(true);
    try {
      if (formData) {
        const userInfo = {
          name: user['custom:name'] || user.name || user.given_name || user.family_name || 'Academic User',
          email: user.email,
          organization:
            user['custom:company'] || user['custom:organization'] || user.organization || 'Not specified',
        };

        await submitCancellationFeedback(formData, userInfo);
      }

      const result = await AcademicExpirationService.cancelAcademicAccount(user.email);

      if (result.success) {
        // Close modal and mark as shown
        setShowWarningModal(false);
        if (warningType) {
          AcademicExpirationService.markWarningShown(warningType);
        }

        // Logout user immediately after cancellation
        const { signOut } = await import('aws-amplify/auth');
        await signOut();

        // Redirect to home page
        window.location.href = '/';

        return { success: true, message: result.message };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      console.error('Error handling cancellation:', error);
      return {
        success: false,
        message: 'An error occurred while processing your cancellation request.',
      };
    } finally {
      setLoading(false);
    }
  };

  const clearPendingRenewalConfirmation = () => {
    const key = getPendingRenewalKey();
    if (key) {
      localStorage.removeItem(key);
      setHasPendingRenewalConfirmation(false);
    }
  };

  /**
   * Dismiss warning modal (user can see it again tomorrow)
   */
  const dismissWarning = async () => {
    setShowWarningModal(false);
    if (warningType) {
      AcademicExpirationService.markWarningShown(warningType);

      // If this is a renewal approval modal, also remove the renewal application tag
      if (warningType === 'renewal-approved' && user?.email) {
        try {
          await AcademicExpirationService.dismissRenewalApproval(user.email);
          // Refresh the page to update user data
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } catch (error) {
          console.error('Error dismissing renewal approval:', error);
        }
      }
    }
  };

  useEffect(() => {
    if (
      showWarningModal &&
      user?.email &&
      warningType &&
      (warningType === 'expired' || warningType === 'expiring-soon' || warningType === 'critical')
    ) {
      if (!userNotifiedAt && !notificationRecordedRef.current) {
        notificationRecordedRef.current = true;
        academicExpirationNotificationService
          .recordExpirationNotification(user.email, userNotifiedAt)
          .then(() => {
            setTimeout(async () => {
              try {
                await forceRefreshUserAttributes();
              } catch (error) {
                console.warn('Failed to refresh user attributes:', error);
              }
            }, 3000);
          })
          .catch(error => {
            console.warn('Failed to record expiration notification:', error);
            notificationRecordedRef.current = false; // Reset on error so it can retry
          });
      }
    }
  }, [showWarningModal, userNotifiedAt, warningType, user?.email]);

  /**
   * Open modal manually (when user clicks on tag)
   */
  const openModal = () => {
    if (!isAcademicExpirationEnabled) return;

    // Determine which modal to show based on current user state (same logic as automatic modal)
    let warnType = null;

    if (wasRenewalApproved) {
      warnType = 'renewal-approved';
    } else if (hasAppliedForRenewal && !wasRenewalDenied) {
      warnType = 'reviewing-application';
    } else if (hasCancelledAccount) {
      warnType = 'account-cancelled';
    } else if (wasRenewalDenied) {
      warnType = 'renewal-denied';
    } else if (expirationStatus) {
      warnType = AcademicExpirationService.getWarningType(expirationStatus);
    }

    if (warnType) {
      setWarningType(warnType);
      setShowWarningModal(true);
    }
  };

  /**
   * Check if user should see expiration tag in profile.
   * Only ever show for academic users (never for Professional/Standard commercial).
   */
  const shouldShowExpirationTag = () => {
    if (!isAcademicExpirationEnabled || !isAcademic || !expirationStatus) return false;
    if (wasRenewalApproved) return false;
    return AcademicExpirationService.shouldShowWarning(expirationStatus);
  };

  /**
   * Get tag display information
   */
  const getTagInfo = () => {
    if (wasRenewalApproved) return null;
    if (!isAcademicExpirationEnabled || !isAcademic || !expirationStatus || !shouldShowExpirationTag())
      return null;

    const { isExpired, daysUntilExpiration, gracePeriodDaysRemaining, hasGracePeriod } = expirationStatus;

    if (isExpired) {
      if (hasGracePeriod) {
        return {
          text: `Account Expired - ${gracePeriodDaysRemaining} days to renew`,
          type: 'danger' as const,
          urgent: gracePeriodDaysRemaining <= 7,
        };
      } else {
        return {
          text: 'Account Expired - Renew to continue access',
          type: 'danger' as const,
          urgent: true,
        };
      }
    } else if (daysUntilExpiration <= 7) {
      return {
        text: `Account expires in ${daysUntilExpiration} days`,
        type: 'danger' as const,
        urgent: true,
      };
    } else {
      return {
        text: `Account expires in ${daysUntilExpiration} days`,
        type: 'warning' as const,
        urgent: false,
      };
    }
  };

  return {
    expirationStatus,
    showWarningModal,
    warningType,
    loading,
    isAcademic,
    shouldShowExpirationTag: shouldShowExpirationTag(),
    tagInfo: getTagInfo(),
    handleRenewal,
    handleCancellation,
    dismissWarning,
    openModal,
    setShowWarningModal,
    clearPendingRenewalConfirmation,
    // New states
    hasAppliedForRenewal,
    hasCancelledAccount,
    wasRenewalDenied,
    wasRenewalApproved,
    currentAcademicPlan,
  };
};
